/**
 * usePushNotifications.js
 * Place in: src/hooks/usePushNotifications.js
 *
 * React hook that:
 *  1. Requests notification permission from the browser
 *  2. Gets the FCM token for this device/browser
 *  3. Saves it to the backend (/api/notifications/token)
 *  4. Handles foreground notifications (app is open and focused)
 *
 * Firebase Cloud Messaging is 100% free — no limits, no cost.
 *
 * USAGE — add to Dashboard.jsx or App.js:
 *   import usePushNotifications from '../hooks/usePushNotifications';
 *   // Inside your component:
 *   usePushNotifications();
 *
 * That's it — one line. The hook handles everything else silently.
 *
 * PREREQUISITES:
 *  1. firebase-messaging-sw.js placed in /public/
 *  2. REACT_APP_FCM_VAPID_KEY in your .env
 *  3. Firebase Cloud Messaging enabled in Firebase Console
 */

import { useEffect, useRef }                          from 'react';
import { getMessaging, getToken, onMessage }          from 'firebase/messaging';
import { auth }                                       from '../firebase';
import { authFetch }                                  from '../utils/authFetch';

// How often to re-register the FCM token (7 days)
// FCM tokens can rotate — refreshing weekly ensures delivery reliability
const TOKEN_REFRESH_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;
const LAST_TOKEN_KEY             = 'hh_fcm_last_registered';

export default function usePushNotifications() {
  const initialized = useRef(false);

  useEffect(() => {
    // Only run once, only when user is logged in
    if (initialized.current || !auth.currentUser) return;
    initialized.current = true;

    const setup = async () => {
      try {
        // ── Step 1: Check if notifications are supported ─────────────────
        if (!('Notification' in window) || !('serviceWorker' in navigator)) {
          console.log('Push notifications not supported in this browser.');
          return;
        }

        // ── Step 2: Don't re-register if we did it recently ──────────────
        const lastRegistered = parseInt(localStorage.getItem(LAST_TOKEN_KEY) || '0');
        if (Date.now() - lastRegistered < TOKEN_REFRESH_INTERVAL_MS) return;

        // ── Step 3: Request permission ────────────────────────────────────
        // Only prompt if not already granted/denied.
        // 'default' = user hasn't decided yet → show the prompt
        // 'granted' = already allowed → get token silently
        // 'denied'  = user blocked → don't ask again
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          console.log(`Push permission: ${permission} — skipping FCM setup`);
          return;
        }

        // ── Step 4: Register service worker ──────────────────────────────
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

        // ── Step 5: Get FCM token ─────────────────────────────────────────
        const messaging = getMessaging();
        const token     = await getToken(messaging, {
          vapidKey          : process.env.REACT_APP_FCM_VAPID_KEY,
          serviceWorkerRegistration: registration,
        });

        if (!token) {
          console.warn('No FCM token received — check VAPID key and service worker.');
          return;
        }

        // ── Step 6: Save token to backend ────────────────────────────────
        await authFetch('/api/notifications/token', {
          method: 'POST',
          body  : JSON.stringify({ fcmToken: token }),
        });

        // Record timestamp so we don't re-register for 7 days
        localStorage.setItem(LAST_TOKEN_KEY, Date.now().toString());
        console.log('FCM push notifications registered successfully.');

        // ── Step 7: Handle foreground notifications ───────────────────────
        // When the app tab is OPEN and focused, Firebase doesn't show a browser
        // notification automatically. We handle it manually here with a toast.
        onMessage(messaging, (payload) => {
          const { title, body } = payload.notification || {};
          if (!title) return;

          // Simple in-app toast notification
          showInAppToast(title, body, payload.fcmOptions?.link);
        });

      } catch (err) {
        // Push setup failing should never break the app — log and move on
        console.warn('Push notification setup failed (non-critical):', err.message);
      }
    };

    setup();
  }, [auth.currentUser?.uid]);
}

/**
 * Show a simple in-app toast for foreground notifications.
 * Appears as a small card in the top-right corner for 5 seconds.
 * No external library needed — pure DOM manipulation.
 */
function showInAppToast(title, body, url) {
  // Remove any existing toast
  const existing = document.getElementById('hh-push-toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.id    = 'hh-push-toast';
  toast.style.cssText = `
    position: fixed; top: 20px; right: 20px; z-index: 99999;
    background: #fff; border: 1px solid #e0e0e0;
    border-left: 4px solid #c95b3d;
    border-radius: 12px; padding: 14px 18px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.15);
    max-width: 320px; cursor: pointer;
    animation: hhToastIn 0.3s ease;
    font-family: inherit;
  `;

  toast.innerHTML = `
    <style>
      @keyframes hhToastIn {
        from { opacity: 0; transform: translateX(20px); }
        to   { opacity: 1; transform: translateX(0); }
      }
    </style>
    <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:10px;">
      <div>
        <div style="font-weight:700; font-size:0.88rem; color:#1a1a1a; margin-bottom:4px;">
          ${title}
        </div>
        ${body ? `<div style="font-size:0.82rem; color:#555; line-height:1.4;">${body}</div>` : ''}
      </div>
      <span id="hh-toast-close" style="color:#aaa; cursor:pointer; font-size:1.1rem; line-height:1; flex-shrink:0;">✕</span>
    </div>
  `;

  // Click → navigate to URL if provided
  toast.addEventListener('click', (e) => {
    if (e.target.id === 'hh-toast-close') { toast.remove(); return; }
    if (url) window.location.href = url;
    toast.remove();
  });

  document.body.appendChild(toast);

  // Auto-dismiss after 5 seconds
  setTimeout(() => { if (toast.parentNode) toast.remove(); }, 5000);
}